import { defineComponent as _defineComponent } from 'vue'
import Toggle from "@/components/bootstrap/Toggle.vue";
import AllergenPropagationTree from "@/components/labelConfiguration/AllergenPropagationTree.vue";
import vModal from "@/components/vModal.vue";
import VAllergenMLModal from "@/components/vAllergenMLModal.vue";
import {useTranslations} from "@/hooks/useTranslations";
import {debounce} from "@/utils/Helpers";
import {computed, inject, ref, watch} from "vue";
import {splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import AllergenRepository, {
  type AllergenPropagationTargetChangeSet as ApiAllergenPropagationTargetChangeSet,
  type AllergenPropagationTarget
} from "@/utils/API/AllergenRepository";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";
import {AllergenDefinition} from "@/models/Allergen/AllergenDefinition";
import {AllergenAlias} from "@/models/Allergen/AllergenAlias";
import {AllergenSelectionStatus, SavedAllergen, SelectedAllergen} from "@/models/Allergen/SelectedAllergen";
import {
  normalizeAllergenSelectionStatus
} from "@/models/Allergen/AllergenSelectionStatusValue";
import {MlAnalysisResponse} from "@/models/AllergenML/AllergenML";
import type { MlAllergenPrediction } from "@/models/AllergenML/AllergenML";
import {NutrientSource} from "@/models/NutrientSource/NutrientSource";
import {AllergenConfigurationTargetType} from "@/models/Allergen/AllergenConfigurationData";
import {formatCountrySpecificAllergenName} from "@/utils/AllergenDisplayNames";
type AllergensConfigurationProps = {
  value: SavedAllergen[],
  language: keyof typeof LabelLanguage,
  mayContainText: Partial<Record<LabelLanguage, string[]>>,
  showPrintoutToggle?: boolean,
  showAllergenOnPrintout?: boolean,
  countryId?: number | null,
  hideAllergenLabel?: boolean,
  showAiDetector?: boolean,
  aiAnalysisPending?: boolean,
  aiTargetType?: "ingredient" | "recipe" | "combined-recipe",
  aiTargetId?: number,
  aiRestaurantId?: number,
  aiTargetName?: string,
  aiCountry?: NutrientSource | null,
  autoSaveOnChange?: boolean,
  isLoading?: boolean,
  aiAnalysisApplied?: boolean,
  externalAiApplyToken?: number,
  showAiDetectorActions?: boolean,
  aiPromptSourceNote?: string,
  beforeOpenAiDetector?: () => boolean | Promise<boolean>,
}

type ToggleVisualState = "default" | "ai" | "user-confirmed" | "user-added" | "user-removed";
type AiPromptState = "pending" | "detected" | "clear";
type AiPromptMascotState = "idle" | "analyzing" | "ready";
type AllergenPropagationChange = {
  allergenId: number,
  allergen: SelectedAllergen | null,
}

type AllergenPropagationTreeNode = AllergenPropagationTarget & {
  parentNodeId: number | null,
  children: AllergenPropagationTreeNode[],
  sortIndex: number,
}

type FlattenedPropagationTarget = AllergenPropagationTarget & {
  depth: number,
  hasChildren: boolean,
  ancestryLabel: string,
}

type PropagationSelectionState = {
  selectedIds: number[],
  isChecked: boolean,
  isIndeterminate: boolean,
}

type PropagationChangePreview = {
  allergenId: number,
  label: string,
  kind: "added" | "removed" | "existing",
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AllergensConfiguration',
  props: {
    value: null,
    language: null,
    mayContainText: null,
    showPrintoutToggle: { type: Boolean },
    showAllergenOnPrintout: { type: Boolean },
    countryId: null,
    hideAllergenLabel: { type: Boolean, default: false },
    showAiDetector: { type: Boolean },
    aiAnalysisPending: { type: Boolean },
    aiTargetType: null,
    aiTargetId: null,
    aiRestaurantId: null,
    aiTargetName: null,
    aiCountry: null,
    autoSaveOnChange: { type: Boolean },
    isLoading: { type: Boolean, default: false },
    aiAnalysisApplied: { type: Boolean },
    externalAiApplyToken: null,
    showAiDetectorActions: { type: Boolean, default: true },
    aiPromptSourceNote: null,
    beforeOpenAiDetector: null
  },
  emits: [
  "fetch-preview",
  "update:value",
  "update:mayContainText",
  "update:showAllergenOnPrintout",
  "update:containsText",
  "save-aliases",
  "ai-analysis-applied",
  "ai-analysis-run-completed",
  "ai-detector-request-finished"
],
  setup(__props: any, { emit }) {

const props = __props as { value: SavedAllergen[], language: keyof typeof LabelLanguage, mayContainText: Partial<Record<LabelLanguage, string[]>>, showPrintoutToggle?: boolean, showAllergenOnPrintout?: boolean, countryId?: number | null, hideAllergenLabel: boolean, showAiDetector?: boolean, aiAnalysisPending?: boolean, aiTargetType?: "ingredient" | "recipe" | "combined-recipe", aiTargetId?: number, aiRestaurantId?: number, aiTargetName?: string, aiCountry?: NutrientSource | null, autoSaveOnChange?: boolean, isLoading: boolean, aiAnalysisApplied?: boolean, externalAiApplyToken?: number, showAiDetectorActions: boolean, aiPromptSourceNote?: string, beforeOpenAiDetector?: () => boolean | Promise<boolean> };

const fetchPreview = inject("bopFetchPreview", () => {}) as () => void;




const translations = useTranslations();

const model = ref<SelectedAllergen[]>([]);
const allergenOptions = ref<AllergenDefinition[]>([]);
const localMayContain = ref<Partial<Record<LabelLanguage, string[]>>>({});
const aliasModalAllergen = ref<AllergenDefinition | null>(null);
const aliasModalSelectedAliases = ref<AllergenAlias[]>([]);
const isAiDetectorModalOpen = ref(false);
const isAiDetectorRunInProgress = ref(false);
const applyingSavedAnalysis = ref(false);
const hasAppliedAiAnalysis = ref(false);
const lastHandledExternalAiApplyToken = ref(0);
const allergenVisualStates = ref<Record<number, ToggleVisualState>>({});
const isPropagationReviewModalOpen = ref(false);
const isApplyingPropagation = ref(false);
const isLoadingPropagationTargets = ref(false);
const isLoadingPropagationDetails = ref(false);
const propagationApplySuccessMessage = ref("");
const pendingPropagationTargets = ref<AllergenPropagationTarget[]>([]);
const pendingPropagationChanges = ref<AllergenPropagationChange[]>([]);
const pendingPropagationBaselineAllergens = ref<SelectedAllergen[]>([]);
const pendingPropagationTargetAllergensById = ref<Record<number, SelectedAllergen[]>>({});
const selectedPropagationTargetIds = ref<number[]>([]);
const disabledPropagationChangeIdsByTargetId = ref<Record<number, number[]>>({});
const propagationRequestId = ref(0);
const pendingManualOverrideAllergen = ref<AllergenDefinition | null>(null);
const languages = computed(() =>
  splitLanguages(LabelLanguage[props.language]) as LabelLanguage[]
);
const isAliasModalOpen = computed(() => aliasModalAllergen.value !== null);
const aliasModalOptions = computed(() => aliasModalAllergen.value?.aliases || []);
const aliasModalTitle = computed(() => {
  return getAliasTooltipText(aliasModalAllergen.value);
});
const containsPreview = computed(() => {
  return model.value
    .filter((selectedAllergen) => selectedAllergen.status !== AllergenSelectionStatus.DetectedByAIDisabledManually)
    .map((selectedAllergen) => {
      const allergen = allergenOptions.value.find((option) => option.id === selectedAllergen.id);
      const allergenName = allergen
        ? getAllergenDisplayName(allergen)
        : selectedAllergen.name || selectedAllergen.code || "";
      const aliases = allergen
        ? getSelectedAliasNames(allergen.id)
        : [];

      if (!allergenName) {
        return "";
      }

      return aliases.length > 0
        ? `${allergenName} (${aliases.join(", ")})`
        : allergenName;
    })
    .filter(Boolean)
    .join(", ");
});
const countryAllergenOptions = computed(() =>
  allergenOptions.value.filter((allergen) =>
    !allergen.isOptionalAllergen && !shouldHideAllergenForCountry(allergen))
);
const optionalAllergenOptions = computed(() =>
  allergenOptions.value.filter((allergen) =>
    allergen.isOptionalAllergen && !shouldHideAllergenForCountry(allergen))
);
const showAiDetectorPrompt = computed(() =>
  Boolean(props.showAiDetector && props.aiRestaurantId && (props.aiTargetId || props.aiTargetType === "ingredient"))
);
const shouldShowAiPromptActions = computed(() =>
  props.showAiDetectorActions !== false
);
const canOpenAiDetector = computed(() =>
  Boolean(props.aiTargetId && props.aiRestaurantId)
);
const effectiveAiAnalysisPending = computed(() =>
  Boolean(isAiDetectorRunInProgress.value && !hasAppliedAiAnalysisState.value && (props.aiAnalysisPending || isAiDetectorModalOpen.value))
);
const hasPersistedAiAnalysisState = computed(() => model.value.some((selectedAllergen) => hasVisibleAiPromptState(selectedAllergen)));
const hasAppliedAiAnalysisState = computed(() =>
  Boolean(props.aiAnalysisApplied || hasAppliedAiAnalysis.value || hasPersistedAiAnalysisState.value)
);
const hasConfiguredAllergenSelections = computed(() =>
  model.value.some((selectedAllergen) =>
    selectedAllergen.status !== AllergenSelectionStatus.DetectedByAIDisabledManually
  )
);
const hasRecipeConfiguredAllergensLoaded = computed(() => Boolean(
  props.hideAllergenLabel
    && props.aiTargetType !== "ingredient"
    && hasConfiguredAllergenSelections.value
    && !hasAppliedAiAnalysisState.value
));
const hasDetectedAiAllergens = computed(() => model.value.some((selectedAllergen) => hasVisibleAiPromptState(selectedAllergen)));
const aiPromptState = computed<AiPromptState>(() => {
  if (effectiveAiAnalysisPending.value) {
    return "pending";
  }

  return hasDetectedAiAllergens.value
    ? "detected"
    : "clear";
});
const aiPromptMascotState = computed<AiPromptMascotState>(() => {
  if (effectiveAiAnalysisPending.value) {
    return "analyzing";
  }

  if (hasAppliedAiAnalysisState.value || hasDetectedAiAllergens.value) {
    return "ready";
  }

  return "idle";
});
const showAiPromptButton = computed(() =>
  Boolean(props.showAiDetector && props.aiRestaurantId && (props.aiTargetId || props.aiTargetType === "ingredient"))
);
const isAiPromptButtonDisabled = computed(() =>
  Boolean(applyingSavedAnalysis.value || effectiveAiAnalysisPending.value)
);
const aiPromptTitle = computed(() => {
  if (effectiveAiAnalysisPending.value) {
    return "Analyzing allergens";
  }

  if (!shouldShowAiPromptActions.value) {
    if (!hasConfiguredAllergenSelections.value) {
      return "Add allergens in recipe settings";
    }

    return "Allergens applied from Recipe settings";
  }

  if (hasAppliedAiAnalysisState.value) {
    return "AI Allergen Results Applied";
  }

  return "Ready to apply AI allergens";
});
const aiPromptDescription = computed(() => {
  if (effectiveAiAnalysisPending.value) {
    return "MenuSano AI is checking ingredients, allergen signals, and label rules for this item.";
  }

  if (!shouldShowAiPromptActions.value && !hasConfiguredAllergenSelections.value) {
    return "To add allergens here automatically, set them up or apply AI in the recipe allergen settings.";
  }

  if (hasAppliedAiAnalysisState.value) {
    if (!shouldShowAiPromptActions.value && props.aiPromptSourceNote) {
      return props.aiPromptSourceNote;
    }

    return "AI allergen results are ready here. Review the toggles below or rerun the detector if you need a fresh pass.";
  }

  if (!shouldShowAiPromptActions.value) {
    return "To add allergens here automatically, set them up or apply AI in the recipe allergen settings.";
  }

  if (hasRecipeConfiguredAllergensLoaded.value) {
    return "These allergen selections are being loaded from the recipe configuration. Review them here or re-apply AI allergens if you want a fresh scan.";
  }

  return "Use AI to scan this item and prepare allergen results for review.";
});
const aiPromptButtonLabel = computed(() => {
  if (effectiveAiAnalysisPending.value) {
    return "Analyzing...";
  }

  if (hasAppliedAiAnalysisState.value) {
    return "Re-Apply AI Allergens";
  }

  if (aiPromptState.value === "detected") {
    return "Apply AI Allergens";
  }

  return "AI Allergen Detect";
});
const aiPromptButtonIconClass = computed(() => {
  if (effectiveAiAnalysisPending.value) {
    return "fa-spinner fa-spin";
  }

  if (hasAppliedAiAnalysisState.value) {
    return "fa-refresh";
  }

  if (aiPromptState.value === "detected") {
    return "fa-check-circle";
  }

  return "fa-magic";
});
const aiDetectorCountry = computed(() =>
  props.aiCountry ?? (props.countryId !== undefined && props.countryId !== null
    ? props.countryId as NutrientSource
    : NutrientSource.US)
);
const allPropagationTargetsSelected = computed(() =>
  pendingPropagationTargets.value.length > 0
  && selectedPropagationTargetIds.value.length === selectablePropagationTargetIds.value.length
);
const propagationTargetCounts = computed(() => {
  return pendingPropagationTargets.value.reduce((counts, target) => {
    if (target.isCombinedRecipe) {
      counts.combinedRecipes += 1;
    }
    else {
      counts.recipes += 1;
    }

    return counts;
  }, {
    recipes: 0,
    combinedRecipes: 0
  });
});
const propagationSummaryLabel = computed(() => {
  const hasCombinedRecipes = propagationTargetCounts.value.combinedRecipes > 0;
  const hasRecipes = propagationTargetCounts.value.recipes > 0;
  const sourceIsCustomIngredient = props.aiTargetType === "ingredient";

  if (sourceIsCustomIngredient) {
    if (hasCombinedRecipes && hasRecipes) {
      return "Affected recipes and combined recipes using this custom ingredient";
    }

    if (hasCombinedRecipes) {
      return "Affected combined recipes using this custom ingredient";
    }

    return "Affected recipes using this custom ingredient";
  }

  if (hasCombinedRecipes && hasRecipes) {
    return "Affected combined recipes and recipes";
  }

  if (hasCombinedRecipes) {
    return "Affected combined recipes";
  }
  return "Affected recipes";
});
const propagationPanelDescription = computed(() =>
  props.aiTargetType === "ingredient"
    ? "Choose which parent recipes or combined recipes using this custom ingredient should receive the same allergen changes."
    : "Choose which parent recipes or combined recipes should receive the same allergen changes."
);
const propagationReviewDisclaimer = computed(() => {
  if (isLoadingPropagationTargets.value) {
    return "We are checking which parent recipes and combined recipes could receive these allergen changes.";
  }

  return pendingPropagationTargets.value.length > 0
    ? `${pendingPropagationTargets.value.length} affected item${pendingPropagationTargets.value.length === 1 ? "" : "s"} found. Review the allergen changes before applying them elsewhere.`
    : "If these allergen changes should flow to parent recipes, review them before applying.";
});
const propagationTargetTree = computed(() =>
  buildPropagationTargetTree(pendingPropagationTargets.value)
);
const flattenedPropagationTargets = computed(() =>
  flattenPropagationTargetTree(propagationTargetTree.value)
);
const selectablePropagationTargetIds = computed(() =>
  flattenedPropagationTargets.value.map((target) => target.id)
);
const propagationSelectionStateById = computed(() =>
  buildPropagationSelectionStateMap(
    propagationTargetTree.value,
    new Set(selectedPropagationTargetIds.value)
  )
);
const isManualOverrideDisclaimerOpen = computed(() =>
  pendingManualOverrideAllergen.value !== null
);
const pendingManualOverrideAllergenName = computed(() =>
  pendingManualOverrideAllergen.value
    ? getAllergenDisplayName(pendingManualOverrideAllergen.value)
    : "this allergen"
);
const showPropagationReviewCallout = computed(() =>
  isLoadingPropagationTargets.value
  || (pendingPropagationTargets.value.length > 0 && pendingPropagationChanges.value.length > 0)
);
const propagationChangePreviewByTargetId = computed(() => {
  const previewByTargetId = new Map<number, PropagationChangePreview[]>();
  const fallbackChanges = buildFallbackPropagationChangePreviewList(pendingPropagationChanges.value);

  for (const target of pendingPropagationTargets.value) {
    const previewChanges = buildPropagationChangePreviewList(
      pendingPropagationTargetAllergensById.value[target.id] || [],
      pendingPropagationChanges.value
    );
    const hasActionablePreviewChanges = previewChanges.some((change) => change.kind !== "existing");

    previewByTargetId.set(
      target.id,
      hasActionablePreviewChanges
        ? previewChanges
        : mergePropagationPreviewChanges(previewChanges, fallbackChanges)
    );
  }

  return previewByTargetId;
});
const hasActionablePropagationChanges = computed(() =>
  pendingPropagationChanges.value.length > 0
  && pendingPropagationTargets.value.length > 0
);

watch(
  () => props.value,
  (newVal) => {
    model.value = normalizeSavedAllergens(newVal || []);
  },
  { deep: true, immediate: true }
);

watch(
  containsPreview,
  (val) => {
    emit("update:containsText", val);
  },
  { immediate: true }
);

watch(
  () => props.countryId,
  () => {
    fetchAllergenOptions();
  },
  { immediate: true }
);

watch(
  () => props.aiTargetId,
  () => {
    isAiDetectorRunInProgress.value = false;
    hasAppliedAiAnalysis.value = false;
    allergenVisualStates.value = {};
    lastHandledExternalAiApplyToken.value = 0;
    clearPendingPropagationSession();
  }
);

watch(
  () => props.externalAiApplyToken,
  async(token) => {
    const nextToken = Number(token || 0);

    if (!nextToken || nextToken === lastHandledExternalAiApplyToken.value || !canOpenAiDetector.value) {
      return;
    }

    lastHandledExternalAiApplyToken.value = nextToken;

    try {
      if (hasAppliedAiAnalysisState.value) {
        await openOrApplyAiDetector();
        return;
      }

      const appliedSavedAnalysis = await applySavedAiAnalysis();

      if (!appliedSavedAnalysis) {
        await openOrApplyAiDetector();
      }
    }
    finally {
      emit("ai-detector-request-finished");
    }
  }
);

watch(
  () => props.mayContainText,
  (val) => {
    localMayContain.value = { ...val };
  },
  { deep: true, immediate: true }
);

watch(
  languages,
  (activeLanguages) => {
    const cleaned: Partial<Record<LabelLanguage, string[]>> = {};

    for (const lang of activeLanguages) {
      if (localMayContain.value[lang]) {
        cleaned[lang] = [...localMayContain.value[lang]!];
      }
    }

    localMayContain.value = cleaned;

    // Notify parent immediately so backend data stays clean
    emit("update:mayContainText", cleaned);
  },
  { immediate: true }
);

const mayContainError = computed(() => {
  if (languages.value.length < 2) return null;

  const [l1, l2] = languages.value;
  const a = localMayContain.value[l1] ?? [];
  const b = localMayContain.value[l2] ?? [];

  return a.length !== b.length
    ? translations.value.get("error_missing_translation")
    : null;
});

async function fetchAllergenOptions() {
  try {
    if (props.countryId !== undefined && props.countryId !== null) {
      const [countryResponse, visibleResponse] = await Promise.all([
        AllergenRepository.listAllergensByCountry(props.countryId),
        AllergenRepository.listAllergens()
      ]);
      const countryAllergenIds = new Set(countryResponse.data.map((allergen) => allergen.id));
      const countryAllergens = countryResponse.data.map((allergen) => ({
        ...allergen,
        isOptionalAllergen: false
      }));
      const optionalAllergens = visibleResponse.data
        .filter((allergen) => !countryAllergenIds.has(allergen.id))
        .map((allergen) => ({
          ...allergen,
          isOptionalAllergen: true
        }));

      allergenOptions.value = [...countryAllergens, ...optionalAllergens];
    }
    else {
      allergenOptions.value = (await AllergenRepository.listAllergens()).data;
    }
    model.value = normalizeSavedAllergens(props.value || []);
    emit('update:value', [...model.value]);
  }
  catch (error) {
    console.error("Failed to load allergens", error);
    allergenOptions.value = [];
  }
}

function getAllergenDisplayName(allergen: AllergenDefinition) {
  const normalizedName = (allergen.name || "").toLowerCase().replace(/[^a-z]/g, "");
  const normalizedCode = (allergen.code || "").toLowerCase().replace(/[^a-z]/g, "");

  if ((props.countryId as NutrientSource | number | null) === NutrientSource.UK
    && (normalizedName === "gluten" || normalizedName === "cerealscontaininggluten" || normalizedCode === "gluten")) {
    return "Cereals containing Gluten";
  }

  return formatCountrySpecificAllergenName(
    translations.value.get(allergen.name) || allergen.name || allergen.code,
    props.countryId as NutrientSource | number | null
  );
}

function shouldHideAllergenForCountry(allergen: AllergenDefinition) {
  const normalizedName = (allergen.name || "").toLowerCase().replace(/[^a-z]/g, "");
  const normalizedCode = (allergen.code || "").toLowerCase().replace(/[^a-z]/g, "");

  return (props.countryId as NutrientSource | number | null) === NutrientSource.UK
    && (normalizedName === "wheat" || normalizedName === "wheattriticale" || normalizedCode === "wheat");
}

function getAliasTooltipText(allergen?: AllergenDefinition | null) {
  if (!allergen) {
    return "Select aliases";
  }

  return `Select ${getAllergenDisplayName(allergen)} Allergens`;
}

function normalizeSavedAllergens(savedAllergens: SavedAllergen[]): SelectedAllergen[] {
  return savedAllergens
    .map((savedAllergen) => {
      if (typeof savedAllergen === "number") {
        const allergen = resolveSavedAllergenOption({ id: savedAllergen });
        return {
          id: allergen?.id || savedAllergen,
          code: allergen?.code,
          name: allergen?.name,
          aliasNames: [],
          status: AllergenSelectionStatus.ManuallyAdded
        };
      }

      if (typeof savedAllergen !== "string") {
        const allergen = resolveSavedAllergenOption(savedAllergen);
        return {
          ...savedAllergen,
          id: allergen?.id || savedAllergen.id,
          code: allergen?.code || savedAllergen.code,
          name: allergen?.name || savedAllergen.name,
          aliasNames: getMatchingAliasIds(allergen, savedAllergen),
          status: normalizeAllergenSelectionStatus(savedAllergen.status)
        };
      }
      const allergen = resolveSavedAllergenOption({ code: savedAllergen, name: savedAllergen });
      return {
        id: allergen?.id || 0,
        code: allergen?.code || savedAllergen,
        name: allergen?.name || savedAllergen,
        aliasNames: [],
        status: AllergenSelectionStatus.ManuallyAdded
      };
    });
}

function resolveSavedAllergenOption(savedAllergen: Partial<SelectedAllergen>) {
  if ((props.countryId as NutrientSource | number | null) !== NutrientSource.UK) {
    return allergenOptions.value.find((option) =>
      option.id === savedAllergen.id
      || option.code === savedAllergen.code
    );
  }

  const normalizedSavedCode = normalizeAiAppliedSourceAllergenCode(
    savedAllergen.code || savedAllergen.name || "",
    props.countryId as NutrientSource | number | null
  );

  const directMatch = allergenOptions.value.find((option) =>
    option.id === savedAllergen.id
    || option.code === savedAllergen.code
  );

  if (directMatch && !shouldHideAllergenForCountry(directMatch)) {
    return directMatch;
  }

  return allergenOptions.value.find((option) =>
    normalizeAiAppliedSourceAllergenCode(
      option.code || option.name || "",
      props.countryId as NutrientSource | number | null
    ) === normalizedSavedCode
  ) || directMatch;
}

function getMatchingAliasIds(allergen: AllergenDefinition | undefined, savedAllergen: SelectedAllergen) {
  const savedAliases = Array.isArray(savedAllergen.aliasNames)
    ? savedAllergen.aliasNames
    : (savedAllergen.aliasName ? [savedAllergen.aliasName] : []);

  if (!allergen) {
    return [];
  }

  return savedAliases
    .map((savedAlias) => {
      if (typeof savedAlias === "number") {
        return savedAlias;
      }

      return (allergen.aliases || []).find((alias) => alias.name === savedAlias)?.id;
    })
    .filter((aliasId): aliasId is number =>
      typeof aliasId === "number" && (allergen.aliases || []).some((alias) => alias.id === aliasId)
    );
}

function hasAiProvenance(selectedAllergen?: SelectedAllergen) {
  if (!selectedAllergen) {
    return false;
  }

  const normalizedStatus = normalizeAllergenSelectionStatus(selectedAllergen.status);

  return normalizedStatus === AllergenSelectionStatus.DetectedByAI
    || normalizedStatus === AllergenSelectionStatus.DetectedByAIDisabledManually
    || Boolean(selectedAllergen.aiRecipeAggregateId);
}

function hasVisibleAiPromptState(selectedAllergen?: SelectedAllergen) {
  if (!selectedAllergen) {
    return false;
  }

  const normalizedStatus = normalizeAllergenSelectionStatus(selectedAllergen.status);

  if (normalizedStatus === AllergenSelectionStatus.DetectedByAIDisabledManually) {
    return false;
  }

  return normalizedStatus === AllergenSelectionStatus.DetectedByAI
    || Boolean(selectedAllergen.aiRecipeAggregateId);
}

function isAllergenSelected(allergenId: number) {
  return model.value.some((selectedAllergen) =>
    selectedAllergen.id === allergenId
    && selectedAllergen.status !== AllergenSelectionStatus.DetectedByAIDisabledManually
  );
}

function getSelectionClass(allergenId: number) {
  const selectedAllergen = model.value.find((allergen) => allergen.id === allergenId);

  if (!selectedAllergen || getToggleVisualState(allergenId) === "user-removed") {
    return "";
  }

  return isAiLikeVisualState(getToggleVisualState(allergenId))
    ? "allergen-row-detected-by-ai"
    : "allergen-row-manual";
}

function isAiLikeVisualState(state: ToggleVisualState) {
  return state === "ai";
}

function getToggleVisualState(allergenId: number): ToggleVisualState {
  const explicitState = allergenVisualStates.value[allergenId];

  if (explicitState) {
    return explicitState;
  }

  const selectedAllergen = model.value.find((allergen) => allergen.id === allergenId);

  if (!selectedAllergen) {
    return "default";
  }

  const normalizedStatus = normalizeAllergenSelectionStatus(selectedAllergen.status);

  if (normalizedStatus === AllergenSelectionStatus.DetectedByAIDisabledManually) {
    return "user-removed";
  }

  if (normalizedStatus === AllergenSelectionStatus.DetectedByAI) {
    return "ai";
  }

  if (normalizedStatus === AllergenSelectionStatus.ManuallyAdded && selectedAllergen.aiRecipeAggregateId) {
    return "ai";
  }

  return "user-added";
}

function getToggleVariant(allergenId: number) {
  return getToggleVisualState(allergenId);
}

function getToggleIconClass(allergenId: number) {
  switch (getToggleVisualState(allergenId)) {
  case "ai":
  case "user-confirmed":
  case "user-added":
    return "fa fa-check";
  case "user-removed":
    return "fa fa-times";
  default:
    return "";
  }
}

function getToggleAccentIconClass(allergenId: number) {
  return getToggleVisualState(allergenId) === "user-added"
    ? "fa fa-question"
    : "";
}

function getToggleStateText(allergenId: number) {
  const visualState = getToggleVisualState(allergenId);

  switch (visualState) {
  case "ai":
    return "Detected by AI.";
  case "user-confirmed":
    return "Manually added as an allergen.";
  case "user-removed":
    return "Detected by AI, then manually disabled.";
  case "user-added":
    return "Manually added as an allergen.";
  default:
    return "";
  }
}

function getSelectedAliases(allergenId: number) {
  const allergen = allergenOptions.value.find((option) => option.id === allergenId);
  const selectedAliasIds = model.value.find((selectedAllergen) => selectedAllergen.id === allergenId)?.aliasNames || [];

  return (allergen?.aliases || []).filter((alias) => selectedAliasIds.includes(alias.id));
}

function getSelectedAliasNames(allergenId: number) {
  return getSelectedAliases(allergenId).map((alias) => alias.name);
}

function hasAliases(allergen: AllergenDefinition) {
  return (allergen.aliases || []).length > 0;
}

function openAliasModal(allergen: AllergenDefinition) {
  aliasModalAllergen.value = allergen;
  aliasModalSelectedAliases.value = getSelectedAliases(allergen.id);
}

function closeAliasModal() {
  aliasModalAllergen.value = null;
  aliasModalSelectedAliases.value = [];
}

function onAliasModalSelectionChanged(aliases: AllergenAlias[]) {
  aliasModalSelectedAliases.value = aliases || [];

  if (!aliasModalAllergen.value) {
    return;
  }

  const previousModel = cloneSelectedAllergens(model.value);
  onAliasesSelected(aliasModalAllergen.value, aliasModalSelectedAliases.value);
  if (!props.autoSaveOnChange) {
    emit("save-aliases");
  }
  const propagationChanges = buildPropagationChanges(previousModel, model.value);
  void persistAllergenConfiguration({ checkPropagation: false, propagationChanges, previousAllergens: previousModel });
}

function saveAliasModal() {
  closeAliasModal();
}

function onAllergenToggled(allergen: AllergenDefinition, isChecked: boolean) {
  if (!isChecked && shouldConfirmManualAiOverride(allergen)) {
    pendingManualOverrideAllergen.value = allergen;
    return;
  }

  applyAllergenToggle(allergen, isChecked);
}

function shouldConfirmManualAiOverride(allergen: AllergenDefinition) {
  const existingAllergen = model.value.find((selectedAllergen) => selectedAllergen.id === allergen.id);
  return Boolean(existingAllergen && hasAiProvenance(existingAllergen));
}

function cancelManualOverrideDisclaimer() {
  pendingManualOverrideAllergen.value = null;
}

function confirmManualOverrideDisclaimer() {
  if (!pendingManualOverrideAllergen.value) {
    return;
  }

  const allergen = pendingManualOverrideAllergen.value;
  pendingManualOverrideAllergen.value = null;
  applyAllergenToggle(allergen, false);
}

function applyAllergenToggle(allergen: AllergenDefinition, isChecked: boolean) {
  const previousModel = cloneSelectedAllergens(model.value);
  const existingAllergen = model.value.find((selectedAllergen) => selectedAllergen.id === allergen.id);
  const currentVisualState = getToggleVisualState(allergen.id);
  const existingIsAiOrigin = hasAiProvenance(existingAllergen);

  if (isChecked) {
    if (existingAllergen) {
      model.value = model.value.map((selectedAllergen) => {
        if (selectedAllergen.id !== allergen.id) {
          return selectedAllergen;
        }

        const normalizedStatus = normalizeAllergenSelectionStatus(selectedAllergen.status);

        return {
          ...selectedAllergen,
          status: normalizedStatus === AllergenSelectionStatus.DetectedByAIDisabledManually
            ? AllergenSelectionStatus.DetectedByAI
            : (existingIsAiOrigin
              ? AllergenSelectionStatus.DetectedByAI
              : AllergenSelectionStatus.ManuallyAdded)
        };
      });

      if (currentVisualState === "user-removed" || existingIsAiOrigin) {
        allergenVisualStates.value = {
          ...allergenVisualStates.value,
          [allergen.id]: "ai"
        };
      }
      else {
        allergenVisualStates.value = {
          ...allergenVisualStates.value,
          [allergen.id]: "user-added"
        };
      }

      emit('update:value', [...model.value]);
      const propagationChanges = buildPropagationChanges(previousModel, model.value);
      void persistAllergenConfiguration({ checkPropagation: true, propagationChanges, previousAllergens: previousModel });
      debouncedFetchPreview();
      return;
    }

    model.value.push({
      id: allergen.id,
      code: allergen.code,
      name: allergen.name,
      aliasNames: [],
      status: AllergenSelectionStatus.ManuallyAdded
    });

    allergenVisualStates.value = {
      ...allergenVisualStates.value,
      [allergen.id]: "user-added"
    };
  }

  else {
    if (existingIsAiOrigin && existingAllergen) {
      model.value = model.value.map((selectedAllergen) => {
        if (selectedAllergen.id !== allergen.id) {
          return selectedAllergen;
        }

        return {
          ...selectedAllergen,
          status: AllergenSelectionStatus.DetectedByAIDisabledManually
        };
      });

      allergenVisualStates.value = {
        ...allergenVisualStates.value,
        [allergen.id]: "user-removed"
      };
    }
    else {
      model.value = model.value.filter((selectedAllergen) => selectedAllergen.id !== allergen.id);
      allergenVisualStates.value = {
        ...allergenVisualStates.value,
        [allergen.id]: "default"
      };
    }
  }

  emit('update:value', [...model.value]);
  const propagationChanges = buildPropagationChanges(previousModel, model.value);
  void persistAllergenConfiguration({ checkPropagation: true, propagationChanges, previousAllergens: previousModel });
  debouncedFetchPreview();
}

function onAliasesSelected(allergen: AllergenDefinition, aliases: AllergenAlias[]) {
  model.value = model.value.map((selectedAllergen) => {
    if (selectedAllergen.id !== allergen.id) {
      return selectedAllergen;
    }
    return {
      ...selectedAllergen,
      aliasNames: (aliases || []).map((alias) => alias.id)
    };
  });
  emit('update:value', [...model.value]);
  debouncedFetchPreview();
}

function applyAiAnalysis(result: MlAnalysisResponse) {
  const previousModel = cloneSelectedAllergens(model.value);
  const previousById = new Map<number, SelectedAllergen>(
    previousModel.map((selectedAllergen) => [selectedAllergen.id, selectedAllergen])
  );
  const aiRecipeAggregateId = props.aiTargetType !== "ingredient"
    ? (result.recipeAggregateId ?? null)
    : null;
  const analysisCountry = (result.nutrientSource ?? aiDetectorCountry.value ?? props.aiCountry ?? props.countryId) as NutrientSource | number | null;
  const manualPredictionCodes = new Set(
    (result.allergens || [])
      .filter((allergen) =>
        allergen.isJurisdictionReportable !== false
        && isUserOverridePrediction(allergen))
      .map((allergen) => normalizeAiAppliedSourceAllergenCode(allergen.id, analysisCountry))
      .filter(Boolean)
  );
  const detectedCodes = new Set(
    (result.allergens || [])
      .filter((allergen) => allergen.isJurisdictionReportable !== false)
      .filter((allergen) => !isUserOverridePrediction(allergen))
      .map((allergen) => normalizeAiAppliedSourceAllergenCode(allergen.id, analysisCountry))
      .filter(Boolean)
  );
  const forcedOnCodes = new Set<string>();
  const forcedOffCodes = new Set<string>();
  const manualOverrideOnCodes = new Set<string>();
  const aiConfirmedOnCodes = new Set<string>();

  for (const ingredient of result.ingredients || []) {
    for (const marker of ingredient.overrideMarkers || []) {
      if (marker.isJurisdictionReportable === false) {
        continue;
      }

      const code = normalizeAiAppliedSourceAllergenCode(marker.allergenCode, analysisCountry);
      if (!code) {
        continue;
      }

      if (marker.isAllergen) {
        forcedOnCodes.add(code);
        forcedOffCodes.delete(code);

        if (marker.wasAiDetected) {
          aiConfirmedOnCodes.add(code);
        }
        else {
          manualOverrideOnCodes.add(code);
        }
      }
      else {
        forcedOffCodes.add(code);
        forcedOnCodes.delete(code);
        manualOverrideOnCodes.delete(code);
        aiConfirmedOnCodes.delete(code);
      }
    }
  }

  const nextById = new Map<number, SelectedAllergen>();

  const shouldAggregateIngredientStates = props.aiTargetType !== "ingredient"
    && (result.ingredients || []).length > 0;

  if (shouldAggregateIngredientStates) {
    const activeIngredientCodes = new Set<string>();
    const aiActiveIngredientCodes = new Set<string>();
    const removedIngredientCodes = new Set<string>();

    for (const ingredient of result.ingredients || []) {
      const ingredientAiDetectedCodes = new Set(
        (ingredient.allergens || [])
          .filter((allergen) => allergen.isJurisdictionReportable !== false)
          .filter((allergen) => !isUserOverridePrediction(allergen))
          .map((allergen) => normalizeAiAppliedSourceAllergenCode(allergen.id, analysisCountry))
          .filter(Boolean)
      );
      const ingredientManualPredictionCodes = new Set(
        (ingredient.allergens || [])
          .filter((allergen) => allergen.isJurisdictionReportable !== false)
          .filter((allergen) => isUserOverridePrediction(allergen))
          .map((allergen) => normalizeAiAppliedSourceAllergenCode(allergen.id, analysisCountry))
          .filter(Boolean)
      );
      const ingredientAiActiveCodes = new Set(ingredientAiDetectedCodes);
      const ingredientActiveCodes = new Set([
        ...ingredientAiDetectedCodes,
        ...ingredientManualPredictionCodes
      ]);
      const ingredientRemovedCodes = new Set<string>();

      for (const marker of ingredient.overrideMarkers || []) {
        if (marker.isJurisdictionReportable === false) {
          continue;
        }

        const code = normalizeAiAppliedSourceAllergenCode(marker.allergenCode, analysisCountry);

        if (!code) {
          continue;
        }

        if (marker.isAllergen) {
          ingredientActiveCodes.add(code);
          ingredientRemovedCodes.delete(code);

          if (marker.wasAiDetected) {
            ingredientAiActiveCodes.add(code);
          }
        }
        else {
          ingredientActiveCodes.delete(code);
          ingredientAiActiveCodes.delete(code);
          ingredientRemovedCodes.add(code);
        }
      }

      for (const code of ingredientActiveCodes) {
        activeIngredientCodes.add(code);
      }

      for (const code of ingredientAiActiveCodes) {
        if (ingredientActiveCodes.has(code)) {
          aiActiveIngredientCodes.add(code);
        }
      }

      for (const code of ingredientRemovedCodes) {
        removedIngredientCodes.add(code);
      }
    }

    for (const allergen of allergenOptions.value) {
      const code = normalizeAllergenCode(allergen.code);
      const existing = previousById.get(allergen.id);

      if (code && activeIngredientCodes.has(code)) {
        nextById.set(
          allergen.id,
          aiActiveIngredientCodes.has(code)
            ? buildAiSelectedAllergen(allergen, AllergenSelectionStatus.DetectedByAI, existing, aiRecipeAggregateId)
            : buildManualSelectedAllergen(allergen, existing)
        );
        continue;
      }

      if (code && removedIngredientCodes.has(code)) {
        nextById.set(allergen.id, buildAiSelectedAllergen(allergen, AllergenSelectionStatus.DetectedByAIDisabledManually, existing, aiRecipeAggregateId));
        continue;
      }

      if (existing?.status === AllergenSelectionStatus.DetectedByAI) {
        nextById.delete(allergen.id);
      }
    }

    const nextVisualStates: Record<number, ToggleVisualState> = {};

    for (const allergen of allergenOptions.value) {
      const code = normalizeAllergenCode(allergen.code);
      const appliedSelection = nextById.get(allergen.id);

      if (isManualAllergenSelection(appliedSelection) && !hasAiProvenance(appliedSelection)) {
        nextVisualStates[allergen.id] = "user-added";
        continue;
      }

      if (code && activeIngredientCodes.has(code)) {
        nextVisualStates[allergen.id] = aiActiveIngredientCodes.has(code)
          ? "ai"
          : "user-added";
        continue;
      }

      if (code && removedIngredientCodes.has(code)) {
        nextVisualStates[allergen.id] = "user-removed";
      }
    }

    model.value = Array.from(nextById.values());
    allergenVisualStates.value = nextVisualStates;
    isAiDetectorRunInProgress.value = false;
    hasAppliedAiAnalysis.value = true;
    emit("update:value", [...model.value]);
    if (!props.autoSaveOnChange) {
      emit("save-aliases");
    }
    emit("ai-analysis-applied", result);
    const propagationChanges = buildPropagationChanges(previousModel, model.value);
    void persistAllergenConfiguration({ checkPropagation: true, propagationChanges, previousAllergens: previousModel });
    debouncedFetchPreview();
    return;
  }

  for (const allergen of allergenOptions.value) {
    const code = normalizeAllergenCode(allergen.code);
    const existing = previousById.get(allergen.id);

    if (code && forcedOffCodes.has(code)) {
      nextById.set(allergen.id, buildAiSelectedAllergen(allergen, AllergenSelectionStatus.DetectedByAIDisabledManually, existing, aiRecipeAggregateId));
      continue;
    }

    if (code && manualOverrideOnCodes.has(code)) {
      nextById.set(allergen.id, buildManualSelectedAllergen(allergen, existing));
      continue;
    }

    if (code && manualPredictionCodes.has(code)) {
      nextById.set(allergen.id, buildManualSelectedAllergen(allergen, existing));
      continue;
    }

    if (code && aiConfirmedOnCodes.has(code)) {
      nextById.set(allergen.id, buildManualSelectedAllergen(allergen, existing, aiRecipeAggregateId));
      continue;
    }

    if (code && (forcedOnCodes.has(code) || detectedCodes.has(code))) {
      nextById.set(allergen.id, buildAiSelectedAllergen(allergen, AllergenSelectionStatus.DetectedByAI, existing, aiRecipeAggregateId));
      continue;
    }
  }

  const nextVisualStates: Record<number, ToggleVisualState> = {};
  const forcedStateByCode = new Map<string, ToggleVisualState>();

  for (const ingredient of result.ingredients || []) {
    for (const marker of ingredient.overrideMarkers || []) {
      if (marker.isJurisdictionReportable === false) {
        continue;
      }

      const code = normalizeAiAppliedSourceAllergenCode(marker.allergenCode, analysisCountry);

      if (!code) {
        continue;
      }

      if (!marker.isAllergen) {
        forcedStateByCode.set(code, "user-removed");
        continue;
      }

      forcedStateByCode.set(code, marker.wasAiDetected ? "ai" : "user-added");
    }
  }

  for (const allergen of allergenOptions.value) {
    const code = normalizeAllergenCode(allergen.code);
    const appliedSelection = nextById.get(allergen.id);
    const forcedState = code ? forcedStateByCode.get(code) : undefined;

    if (isManualAllergenSelection(appliedSelection) && !hasAiProvenance(appliedSelection)) {
      nextVisualStates[allergen.id] = "user-added";
      continue;
    }

    if (forcedState) {
      nextVisualStates[allergen.id] = forcedState;
      continue;
    }

    if (code && detectedCodes.has(code)) {
      nextVisualStates[allergen.id] = "ai";
    }
  }

  model.value = Array.from(nextById.values());
  allergenVisualStates.value = nextVisualStates;
  isAiDetectorRunInProgress.value = false;
  hasAppliedAiAnalysis.value = true;
  emit("update:value", [...model.value]);
  if (!props.autoSaveOnChange) {
    emit("save-aliases");
  }
  emit("ai-analysis-applied", result);
  const propagationChanges = buildPropagationChanges(previousModel, model.value);
  void persistAllergenConfiguration({ checkPropagation: true, propagationChanges, previousAllergens: previousModel });
  debouncedFetchPreview();
}

async function openOrApplyAiDetector() {
  if (props.beforeOpenAiDetector) {
    const canOpen = await props.beforeOpenAiDetector();

    if (!canOpen) {
      return;
    }
  }

  if (hasAppliedAiAnalysisState.value) {
    isAiDetectorModalOpen.value = true;
    return;
  }

  const appliedSavedAnalysis = await applySavedAiAnalysis();

  if (appliedSavedAnalysis) {
    return;
  }

  isAiDetectorRunInProgress.value = true;
  isAiDetectorModalOpen.value = true;
}

function onAiDetectorModalClosed() {
  isAiDetectorModalOpen.value = false;

  if (!hasAppliedAiAnalysisState.value) {
    isAiDetectorRunInProgress.value = false;
  }
}

async function applySavedAiAnalysis() {
  if (!props.aiTargetId || !props.aiRestaurantId || applyingSavedAnalysis.value) {
    return false;
  }

  applyingSavedAnalysis.value = true;

  try {
    const response = props.aiTargetType === "ingredient"
      ? await AllergenMLRepository.getSavedIngredientAnalysis({
        restaurantId: props.aiRestaurantId,
        ingredientId: props.aiTargetId,
        countryId: aiDetectorCountry.value,
        nutrientSource: aiDetectorCountry.value
      })
      : await AllergenMLRepository.getSavedRecipeAnalysis({
        restaurantId: props.aiRestaurantId,
        recipeId: props.aiTargetId,
        isCombinedRecipe: props.aiTargetType === "combined-recipe",
        countryId: aiDetectorCountry.value,
        nutrientSource: aiDetectorCountry.value
      });

    applyAiAnalysis(response.data);
    return true;
  }
  catch (error) {
    console.error("Failed to apply saved allergen analysis", error);
    return false;
  }
  finally {
    applyingSavedAnalysis.value = false;
  }
}

async function persistAllergenConfiguration(options: {
  checkPropagation?: boolean;
  propagationChanges?: AllergenPropagationChange[];
  previousAllergens?: SelectedAllergen[];
} = {}) {
  if (!props.autoSaveOnChange || !props.aiTargetId) {
    return;
  }

  const targetType = getConfigurationTargetType();
  const shouldCheckPropagation = Boolean(
    options.checkPropagation
    && options.propagationChanges?.length
    && shouldOfferPropagation(targetType)
  );
  const saveRequest = {
    targetType,
    targetId: props.aiTargetId,
    allergens: [...model.value],
    mayContainText: { ...localMayContain.value }
  };

  try {
    await AllergenRepository.saveAllergenConfiguration(saveRequest);

    if (shouldCheckPropagation) {
      void refreshPendingPropagationSession(
        targetType,
        options.propagationChanges || [],
        options.previousAllergens || []
      );
    }
    else if (options.checkPropagation && !isPropagationReviewModalOpen.value && !isLoadingPropagationTargets.value) {
      clearPendingPropagationSession();
    }
  }
  catch {
    return;
  }
}

function getConfigurationTargetType() {
  return props.aiTargetType === "ingredient"
    ? AllergenConfigurationTargetType.Ingredient
    : AllergenConfigurationTargetType.Recipe;
}

function shouldOfferPropagation(targetType: AllergenConfigurationTargetType) {
  return Boolean(props.aiTargetId)
    && !isApplyingPropagation.value
    && (targetType === AllergenConfigurationTargetType.Ingredient
      || props.aiTargetType === "recipe"
      || props.aiTargetType === "combined-recipe");
}

async function refreshPendingPropagationSession(
  targetType: AllergenConfigurationTargetType,
  propagationChanges: AllergenPropagationChange[],
  previousAllergens: SelectedAllergen[],
  forceRefresh = false
) {
  if (!props.aiTargetId || !propagationChanges.length) {
    clearPendingPropagationSession();
    return;
  }

  if (!pendingPropagationBaselineAllergens.value.length) {
    pendingPropagationBaselineAllergens.value = cloneSelectedAllergens(previousAllergens);
  }

  const effectiveChanges = buildPropagationChanges(
    pendingPropagationBaselineAllergens.value,
    model.value
  );

  if (!effectiveChanges.length) {
    clearPendingPropagationSession();
    return;
  }

  pendingPropagationChanges.value = effectiveChanges.map((change) => ({
    allergenId: change.allergenId,
    allergen: change.allergen ? cloneSelectedAllergen(change.allergen) : null
  }));

  if (!forceRefresh && (pendingPropagationTargets.value.length > 0 || isLoadingPropagationTargets.value)) {
    return;
  }

  const requestId = propagationRequestId.value + 1;
  propagationRequestId.value = requestId;
  pendingPropagationTargets.value = [];
  selectedPropagationTargetIds.value = [];
  disabledPropagationChangeIdsByTargetId.value = {};
  isLoadingPropagationTargets.value = true;

  try {
    const response = await AllergenRepository.getPropagationTargets(targetType, props.aiTargetId);
    if (requestId !== propagationRequestId.value) {
      return;
    }

    await applyLoadedPropagationTargets(
      (response.data || []).filter((target) => target.id !== props.aiTargetId)
    );
  }
  catch {
    if (requestId === propagationRequestId.value) {
      clearPendingPropagationSession();
    }
  }
  finally {
    if (requestId === propagationRequestId.value) {
      isLoadingPropagationTargets.value = false;
    }
  }
}

async function applyLoadedPropagationTargets(
  targets: AllergenPropagationTarget[]
) {
  if (!targets.length || !pendingPropagationChanges.value.length) {
    clearPendingPropagationSession();
    return;
  }

  const currentTargetAllergensById = pendingPropagationTargetAllergensById.value;
  const nextTargetAllergensById: Record<number, SelectedAllergen[]> = {};

  for (const target of targets) {
    nextTargetAllergensById[target.id] = currentTargetAllergensById[target.id]
      ?? normalizeSavedAllergens(target.configuration?.allergens || []);
  }

  const nextDisabledChangeIdsByTargetId: Record<number, number[]> = {};

  for (const target of targets) {
    nextDisabledChangeIdsByTargetId[target.id] = [];
  }

  pendingPropagationTargets.value = targets;
  selectedPropagationTargetIds.value = targets.map((target) => target.id);
  pendingPropagationTargetAllergensById.value = nextTargetAllergensById;
  disabledPropagationChangeIdsByTargetId.value = nextDisabledChangeIdsByTargetId;
}

function clearPendingPropagationSession() {
  isPropagationReviewModalOpen.value = false;
  propagationApplySuccessMessage.value = "";
  pendingPropagationTargets.value = [];
  pendingPropagationChanges.value = [];
  pendingPropagationBaselineAllergens.value = [];
  pendingPropagationTargetAllergensById.value = {};
  selectedPropagationTargetIds.value = [];
  disabledPropagationChangeIdsByTargetId.value = {};
  isLoadingPropagationTargets.value = false;
  isLoadingPropagationDetails.value = false;
}

function skipPropagationReview() {
  clearPendingPropagationSession();
}

function openPropagationReviewModal() {
  if (isLoadingPropagationTargets.value || !props.aiTargetId) {
    return;
  }

  if (!pendingPropagationTargets.value.length || isLoadingPropagationTargets.value) {
    return;
  }

  isPropagationReviewModalOpen.value = true;
  void ensurePropagationTargetAllergensLoaded();
}

function closePropagationReviewModal() {
  isPropagationReviewModalOpen.value = false;
}

function onPropagationSelectAllChanged(event: Event) {
  const checked = (event.target as HTMLInputElement).checked;

  selectedPropagationTargetIds.value = checked
    ? [...selectablePropagationTargetIds.value]
    : [];
}

function updatePropagationTargetSelection(targetId: number, checked: boolean) {
  const nextSelectedIds = new Set(selectedPropagationTargetIds.value);
  const affectedIds = propagationSelectionStateById.value.get(targetId)?.selectedIds || [targetId];

  if (checked) {
    affectedIds.forEach((id) => nextSelectedIds.add(id));
    selectedPropagationTargetIds.value = selectablePropagationTargetIds.value.filter((id) => nextSelectedIds.has(id));
    return;
  }

  affectedIds.forEach((id) => nextSelectedIds.delete(id));
  selectedPropagationTargetIds.value = selectablePropagationTargetIds.value.filter((id) => nextSelectedIds.has(id));
}

function onPropagationTreeToggled(payload: { targetId: number, checked: boolean }) {
  updatePropagationTargetSelection(payload.targetId, payload.checked);
}

function onPropagationTargetChangeToggled(payload: { targetId: number, allergenId: number }) {
  if (!selectedPropagationTargetIds.value.includes(payload.targetId)) {
    return;
  }

  const currentDisabledIds = new Set(disabledPropagationChangeIdsByTargetId.value[payload.targetId] || []);

  if (currentDisabledIds.has(payload.allergenId)) {
    currentDisabledIds.delete(payload.allergenId);
  }
  else {
    currentDisabledIds.add(payload.allergenId);
  }

  disabledPropagationChangeIdsByTargetId.value = {
    ...disabledPropagationChangeIdsByTargetId.value,
    [payload.targetId]: Array.from(currentDisabledIds)
  };
}

async function applyPropagationChangesToTargets() {
  const targetChangeSets = buildPropagationTargetChangeSets();

  if (!targetChangeSets.length) {
    clearPendingPropagationSession();
    return;
  }

  isApplyingPropagation.value = true;

  try {
    await AllergenRepository.applyPropagationChanges(targetChangeSets);
    propagationApplySuccessMessage.value = "Allergen changes applied to selected items.";
    window.setTimeout(() => {
      clearPendingPropagationSession();
    }, 1000);
  }
  catch {}
  finally {
    isApplyingPropagation.value = false;
  }
}

function isManualAllergenSelection(selectedAllergen?: SelectedAllergen) {
  return selectedAllergen?.status === AllergenSelectionStatus.ManuallyAdded
    || selectedAllergen?.status === AllergenSelectionStatus.DetectedByAIDisabledManually;
}

function buildManualSelectedAllergen(
  allergen: AllergenDefinition,
  existing?: SelectedAllergen,
  aiRecipeAggregateId?: number | null
): SelectedAllergen {
  return {
    id: allergen.id,
    code: allergen.code,
    name: allergen.name,
    aliasNames: existing?.aliasNames || [],
    status: AllergenSelectionStatus.ManuallyAdded,
    aiRecipeAggregateId: aiRecipeAggregateId ?? null
  };
}

function buildAiSelectedAllergen(
  allergen: AllergenDefinition,
  status: AllergenSelectionStatus,
  existing?: SelectedAllergen,
  aiRecipeAggregateId?: number | null
): SelectedAllergen {
  return {
    id: allergen.id,
    code: allergen.code,
    name: allergen.name,
    aliasNames: existing?.aliasNames || [],
    status,
    aiRecipeAggregateId: aiRecipeAggregateId ?? existing?.aiRecipeAggregateId ?? null
  };
}

function normalizeAllergenCode(code?: string | null) {
  return (code || "").trim().toLowerCase();
}

function normalizeAiAppliedSourceAllergenCode(
  code?: string | null,
  country?: NutrientSource | number | null
) {
  const normalizedCode = normalizeAllergenCode(code);

  if ((country as NutrientSource | number | null) === NutrientSource.UK) {
    switch (normalizedCode) {
    case "gluten":
    case "cerealscontaininggluten":
    case "cerealsthatcontaingluten":
    case "wheat":
    case "triticale":
    case "wheatandtriticale":
    case "wheattriticale":
      return "gluten";
    default:
      return normalizedCode;
    }
  }

  return normalizedCode;
}

function isUserOverridePrediction(allergen: MlAllergenPrediction) {
  const source = (allergen.source || "").toLowerCase();
  const evidenceType = (allergen.primaryEvidenceType || "").toLowerCase();
  const evidenceText = (allergen.evidence || []).join(" ").toLowerCase();

  return source.indexOf("override") !== -1
    || evidenceType === "override"
    || evidenceText.indexOf("manual") !== -1;
}

function onTagAdded() {
  emit("update:mayContainText",{ ...localMayContain.value });
  void persistAllergenConfiguration();

  if (!mayContainError.value) {
    debouncedFetchPreview();
  }
}

function cloneSelectedAllergen(allergen: SelectedAllergen): SelectedAllergen {
  return {
    ...allergen,
    aliasNames: [...(allergen.aliasNames || [])]
  };
}

function buildPropagationTargetTree(targets: AllergenPropagationTarget[]) {
  if (!targets.length) {
    return [] as AllergenPropagationTreeNode[];
  }

  const nodes: AllergenPropagationTreeNode[] = targets.map((target, index) => ({
    ...target,
    parentNodeId: getPropagationParentId(target),
    children: [] as AllergenPropagationTreeNode[],
    sortIndex: index
  }));
  const parentLinkedTree = buildPropagationTreeFromParents(nodes);

  if (parentLinkedTree) {
    return sortPropagationTree(parentLinkedTree);
  }

  const depthHints = targets.map(getPropagationDepthHint);
  const depthHintTree = buildPropagationTreeFromDepthHints(nodes, depthHints);

  if (depthHintTree) {
    return sortPropagationTree(depthHintTree);
  }

  return sortPropagationTree(nodes);
}

function buildPropagationTreeFromParents(nodes: AllergenPropagationTreeNode[]): AllergenPropagationTreeNode[] | null {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const roots: AllergenPropagationTreeNode[] = [];
  let linkedByParentId = false;

  for (const node of nodes) {
    node.children = [];

    if (
      node.parentNodeId
      && nodesById.has(node.parentNodeId)
      && !createsPropagationParentCycle(node.id, node.parentNodeId, nodesById)
    ) {
      nodesById.get(node.parentNodeId)?.children.push(node);
      linkedByParentId = true;
      continue;
    }

    roots.push(node);
  }

  if (!linkedByParentId || !roots.length) {
    return null;
  }

  return roots;
}

function createsPropagationParentCycle(
  nodeId: number,
  parentNodeId: number,
  nodesById: Map<number, AllergenPropagationTreeNode>
) {
  let currentParentId: number | null = parentNodeId;
  const visitedParentIds = new Set<number>();

  while (typeof currentParentId === "number" && currentParentId > 0) {
    if (currentParentId === nodeId || visitedParentIds.has(currentParentId)) {
      return true;
    }

    visitedParentIds.add(currentParentId);
    currentParentId = nodesById.get(currentParentId)?.parentNodeId ?? null;
  }

  return false;
}

function buildPropagationTreeFromDepthHints(
  nodes: AllergenPropagationTreeNode[],
  depthHints: number[]
): AllergenPropagationTreeNode[] | null {
  if (!depthHints.some((depth) => depth > 0)) {
    return null;
  }

  const rebuiltRoots: AllergenPropagationTreeNode[] = [];
  const depthStack: AllergenPropagationTreeNode[] = [];

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const depth = Math.max(0, depthHints[index]);

    node.children = [];

    while (depthStack.length > depth) {
      depthStack.pop();
    }

    if (depth > 0 && depthStack.length > 0) {
      depthStack[depthStack.length - 1].children.push(node);
    }
    else {
      rebuiltRoots.push(node);
    }

    depthStack[depth] = node;
  }

  return rebuiltRoots;
}

function sortPropagationTree(nodes: AllergenPropagationTreeNode[]): AllergenPropagationTreeNode[] {
  return [...nodes]
    .sort((left, right) => left.sortIndex - right.sortIndex)
    .map((node) => ({
      ...node,
      children: sortPropagationTree(node.children)
    }));
}

function flattenPropagationTargetTree(
  nodes: AllergenPropagationTreeNode[],
  depth = 0,
  parentNames: string[] = []
): FlattenedPropagationTarget[] {
  const flattenedNodes: FlattenedPropagationTarget[] = [];

  for (const node of nodes) {
    const ancestryLabel = parentNames.length
      ? `Affected through ${parentNames.join(" -> ")}`
      : "";
    const flattenedNode: FlattenedPropagationTarget = {
      ...node,
      depth,
      hasChildren: node.children.length > 0,
      ancestryLabel
    };

    flattenedNodes.push(flattenedNode);
    flattenedNodes.push(
      ...flattenPropagationTargetTree(node.children, depth + 1, [...parentNames, node.name])
    );
  }

  return flattenedNodes;
}

function buildPropagationSelectionStateMap(
  nodes: AllergenPropagationTreeNode[],
  selectedIds: Set<number>
) {
  const selectionStateById = new Map<number, PropagationSelectionState>();

  for (const node of nodes) {
    addPropagationSelectionState(node, selectedIds, selectionStateById);
  }

  return selectionStateById;
}

function addPropagationSelectionState(
  node: AllergenPropagationTreeNode,
  selectedIds: Set<number>,
  selectionStateById: Map<number, PropagationSelectionState>
) {
  const descendantIds: number[] = [];

  for (const child of node.children) {
    descendantIds.push(...addPropagationSelectionState(child, selectedIds, selectionStateById));
  }

  const branchIds = [node.id, ...descendantIds];
  const selectedCount = branchIds.filter((id) => selectedIds.has(id)).length;

  selectionStateById.set(node.id, {
    selectedIds: branchIds,
    isChecked: branchIds.length > 0 && selectedCount === branchIds.length,
    isIndeterminate: selectedCount > 0 && selectedCount < branchIds.length
  });

  return branchIds;
}

function getPropagationParentId(target: AllergenPropagationTarget) {
  const directParentId = [
    target.parentId,
    target.parentRecipeId,
    target.parentTargetId,
    target.ancestorId
  ].find((value): value is number =>
    typeof value === "number" && value > 0 && value !== target.id
  );

  if (typeof directParentId === "number") {
    return directParentId;
  }

  const lineageIds = extractPropagationLineageIds(target.lineagePath || target.path || null);

  if (!lineageIds.length) {
    return null;
  }

  const lineageParentId = lineageIds[lineageIds.length - 1] === target.id
    ? lineageIds[lineageIds.length - 2]
    : lineageIds[lineageIds.length - 1];

  return typeof lineageParentId === "number" && lineageParentId > 0 && lineageParentId !== target.id
    ? lineageParentId
    : null;
}

function getPropagationDepthHint(target: AllergenPropagationTarget) {
  const depth = [target.depth, target.level].find((value): value is number =>
    typeof value === "number" && value >= 0
  );

  if (typeof depth === "number") {
    return depth;
  }

  const lineageIds = extractPropagationLineageIds(target.lineagePath || target.path || null);

  return lineageIds.length;
}

function extractPropagationLineageIds(rawPath?: string | null) {
  if (!rawPath) {
    return [] as number[];
  }

  return rawPath
    .split(/[^\d]+/)
    .map((segment) => Number(segment))
    .filter((segment) => Number.isFinite(segment) && segment > 0);
}

function cloneSelectedAllergens(allergens: SelectedAllergen[]) {
  return allergens.map(cloneSelectedAllergen);
}

function buildPropagationChanges(previousAllergens: SelectedAllergen[], nextAllergens: SelectedAllergen[]) {
  const previousById = new Map(previousAllergens.map((allergen) => [allergen.id, allergen]));
  const nextById = new Map(nextAllergens.map((allergen) => [allergen.id, allergen]));
  const changedIds = new Set<number>([
    ...previousById.keys(),
    ...nextById.keys()
  ]);

  return Array.from(changedIds)
    .filter((id) => !areSelectedAllergensEqual(previousById.get(id), nextById.get(id)))
    .map((id) => ({
      allergenId: id,
      allergen: nextById.has(id)
        ? cloneSelectedAllergen(nextById.get(id) as SelectedAllergen)
        : null
    }));
}

function areSelectedAllergensEqual(left?: SelectedAllergen, right?: SelectedAllergen) {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.id === right.id
    && left.status === right.status
    && left.aiRecipeAggregateId === right.aiRecipeAggregateId
    && normalizeAliasIds(left.aliasNames).join(",") === normalizeAliasIds(right.aliasNames).join(",");
}

function normalizeAliasIds(aliasIds?: number[]) {
  return [...(aliasIds || [])].sort((left, right) => left - right);
}

async function loadPropagationTargetAllergens(targets: AllergenPropagationTarget[]) {
  if (!targets.length) {
    return {} as Record<number, SelectedAllergen[]>;
  }

  try {
    const response = await AllergenRepository.getStoredAllergenConfigurations(
      AllergenConfigurationTargetType.Recipe,
      targets.map((target) => target.id)
    );

    const targetAllergensById: Record<number, SelectedAllergen[]> = {};

    for (const target of targets) {
      const matchingConfiguration = (response.data || []).find((item) => item.targetId === target.id)?.configuration;
      targetAllergensById[target.id] = normalizeSavedAllergens(
        matchingConfiguration?.allergens || target.configuration?.allergens || []
      );
    }

    return targetAllergensById;
  }
  catch {
    const fallbackTargetAllergensById: Record<number, SelectedAllergen[]> = {};

    for (const target of targets) {
      fallbackTargetAllergensById[target.id] = normalizeSavedAllergens(target.configuration?.allergens || []);
    }

    return fallbackTargetAllergensById;
  }
}

async function ensurePropagationTargetAllergensLoaded() {
  if (!pendingPropagationTargets.value.length || isLoadingPropagationDetails.value) {
    return;
  }

  const missingTargets = pendingPropagationTargets.value.filter((target) =>
    pendingPropagationTargetAllergensById.value[target.id] === undefined
  );

  if (!missingTargets.length) {
    return;
  }

  isLoadingPropagationDetails.value = true;

  try {
    const loadedConfigurations = await loadPropagationTargetAllergens(missingTargets);
    pendingPropagationTargetAllergensById.value = {
      ...pendingPropagationTargetAllergensById.value,
      ...loadedConfigurations
    };
  }
  finally {
    isLoadingPropagationDetails.value = false;
  }
}

function buildPropagationChangePreviewList(
  currentTargetAllergens: SelectedAllergen[],
  changes: AllergenPropagationChange[]
) {
  const currentById = new Map(currentTargetAllergens.map((allergen) => [allergen.id, allergen]));
  const currentEnabledIds = new Set(
    currentTargetAllergens
      .filter((allergen) =>
        normalizeAllergenSelectionStatus(allergen.status) !== AllergenSelectionStatus.DetectedByAIDisabledManually
      )
      .map((allergen) => allergen.id)
  );
  const nextEnabledIds = new Set(
    mergePropagationChanges(currentTargetAllergens, changes)
      .filter((allergen) =>
        normalizeAllergenSelectionStatus(allergen.status) !== AllergenSelectionStatus.DetectedByAIDisabledManually
      )
      .map((allergen) => allergen.id)
  );
  const changeById = new Map(changes.map((change) => [change.allergenId, change]));
  const additionCandidateIds = changes
    .filter((change) => !isPropagationRemovalChange(change))
    .map((change) => change.allergenId);
  const removalCandidateIds = changes
    .filter((change) => isPropagationRemovalChange(change) && currentEnabledIds.has(change.allergenId))
    .map((change) => change.allergenId);
  const candidateIds = new Set<number>([
    ...currentEnabledIds,
    ...nextEnabledIds,
    ...additionCandidateIds,
    ...removalCandidateIds
  ]);

  return allergenOptions.value
    .filter((allergen) => candidateIds.has(allergen.id))
    .map((allergen) => {
      const existedBefore = currentEnabledIds.has(allergen.id);
      const existsAfter = nextEnabledIds.has(allergen.id);
      const change = changeById.get(allergen.id);
      const currentStatus = normalizeAllergenSelectionStatus(currentById.get(allergen.id)?.status);
      const isAlreadyExplicitlyRemoved = currentStatus === AllergenSelectionStatus.DetectedByAIDisabledManually;

      let kind: PropagationChangePreview["kind"] = "existing";
      if (change) {
        if (isPropagationRemovalChange(change)) {
          kind = existedBefore && !isAlreadyExplicitlyRemoved ? "removed" : "existing";
        }
        else {
          kind = existedBefore ? "existing" : "added";
        }
      }
      else if (!existedBefore && existsAfter) {
        kind = "added";
      }
      else if (existedBefore && !existsAfter) {
        kind = "removed";
      }

      return {
        allergenId: allergen.id,
        label: getPropagationChangeLabel(change, allergen.id),
        kind
      };
    });
}

function buildFallbackPropagationChangePreviewList(changes: AllergenPropagationChange[]) {
  return changes
    .map((change) => ({
      allergenId: change.allergenId,
      label: getPropagationChangeLabel(change, change.allergenId),
      kind: isPropagationRemovalChange(change) ? "removed" : "added"
    } as PropagationChangePreview))
    .filter((change, index, list) =>
      list.findIndex((candidate) => candidate.allergenId === change.allergenId) === index
    );
}

function mergePropagationPreviewChanges(
  existingChanges: PropagationChangePreview[],
  fallbackChanges: PropagationChangePreview[]
) {
  if (!existingChanges.length) {
    return fallbackChanges;
  }

  if (!fallbackChanges.length) {
    return existingChanges;
  }

  const fallbackById = new Map(
    fallbackChanges.map((change) => [change.allergenId, change] as const)
  );
  const mergedChanges: PropagationChangePreview[] = [];

  for (const existingChange of existingChanges) {
    const fallbackChange = fallbackById.get(existingChange.allergenId);

    if (fallbackChange) {
      mergedChanges.push(fallbackChange);
      fallbackById.delete(existingChange.allergenId);
      continue;
    }

    mergedChanges.push(existingChange);
  }

  return [
    ...mergedChanges,
    ...Array.from(fallbackById.values())
  ];
}

function isPropagationRemovalChange(change: AllergenPropagationChange) {
  return !change.allergen
    || normalizeAllergenSelectionStatus(change.allergen.status) === AllergenSelectionStatus.DetectedByAIDisabledManually;
}

function getPropagationChangeLabel(change: AllergenPropagationChange | undefined, allergenId: number) {
  const matchingOption = allergenOptions.value.find((allergen) => allergen.id === allergenId);

  return matchingOption
    ? getAllergenDisplayName(matchingOption)
    : (change?.allergen?.name || change?.allergen?.code || `Allergen ${allergenId}`);
}

function buildPropagationTargetChangeSets() {
  return pendingPropagationTargets.value
    .filter((target) => selectedPropagationTargetIds.value.includes(target.id))
    .map((target) => {
      const disabledIds = new Set(disabledPropagationChangeIdsByTargetId.value[target.id] || []);
      const changes = pendingPropagationChanges.value
        .filter((change) => !disabledIds.has(change.allergenId))
        .map((change) => ({
          allergenId: change.allergenId,
          allergen: change.allergen ? cloneSelectedAllergen(change.allergen) : null
        }));

      return {
        targetId: target.id,
        changes
      };
    })
    .filter((target): target is ApiAllergenPropagationTargetChangeSet =>
      target.changes.length > 0
    );
}

function mergePropagationChanges(currentAllergens: SelectedAllergen[], propagationChanges: AllergenPropagationChange[]) {
  const allergensById = new Map(currentAllergens.map((allergen) => [allergen.id, cloneSelectedAllergen(allergen)]));

  for (const change of propagationChanges) {
    if (!change.allergen) {
      allergensById.delete(change.allergenId);
      continue;
    }

    allergensById.set(change.allergenId, cloneSelectedAllergen(change.allergen));
  }

  return Array.from(allergensById.values());
}

function onShowAllergenOnPrintoutChanged(isChecked: boolean) {
  emit("update:showAllergenOnPrintout", isChecked);
}

function debouncedFetchPreview() {
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

return { __sfc: true,fetchPreview, props, emit, translations, model, allergenOptions, localMayContain, aliasModalAllergen, aliasModalSelectedAliases, isAiDetectorModalOpen, isAiDetectorRunInProgress, applyingSavedAnalysis, hasAppliedAiAnalysis, lastHandledExternalAiApplyToken, allergenVisualStates, isPropagationReviewModalOpen, isApplyingPropagation, isLoadingPropagationTargets, isLoadingPropagationDetails, propagationApplySuccessMessage, pendingPropagationTargets, pendingPropagationChanges, pendingPropagationBaselineAllergens, pendingPropagationTargetAllergensById, selectedPropagationTargetIds, disabledPropagationChangeIdsByTargetId, propagationRequestId, pendingManualOverrideAllergen, languages, isAliasModalOpen, aliasModalOptions, aliasModalTitle, containsPreview, countryAllergenOptions, optionalAllergenOptions, showAiDetectorPrompt, shouldShowAiPromptActions, canOpenAiDetector, effectiveAiAnalysisPending, hasPersistedAiAnalysisState, hasAppliedAiAnalysisState, hasConfiguredAllergenSelections, hasRecipeConfiguredAllergensLoaded, hasDetectedAiAllergens, aiPromptState, aiPromptMascotState, showAiPromptButton, isAiPromptButtonDisabled, aiPromptTitle, aiPromptDescription, aiPromptButtonLabel, aiPromptButtonIconClass, aiDetectorCountry, allPropagationTargetsSelected, propagationTargetCounts, propagationSummaryLabel, propagationPanelDescription, propagationReviewDisclaimer, propagationTargetTree, flattenedPropagationTargets, selectablePropagationTargetIds, propagationSelectionStateById, isManualOverrideDisclaimerOpen, pendingManualOverrideAllergenName, showPropagationReviewCallout, propagationChangePreviewByTargetId, hasActionablePropagationChanges, mayContainError, fetchAllergenOptions, getAllergenDisplayName, shouldHideAllergenForCountry, getAliasTooltipText, normalizeSavedAllergens, resolveSavedAllergenOption, getMatchingAliasIds, hasAiProvenance, hasVisibleAiPromptState, isAllergenSelected, getSelectionClass, isAiLikeVisualState, getToggleVisualState, getToggleVariant, getToggleIconClass, getToggleAccentIconClass, getToggleStateText, getSelectedAliases, getSelectedAliasNames, hasAliases, openAliasModal, closeAliasModal, onAliasModalSelectionChanged, saveAliasModal, onAllergenToggled, shouldConfirmManualAiOverride, cancelManualOverrideDisclaimer, confirmManualOverrideDisclaimer, applyAllergenToggle, onAliasesSelected, applyAiAnalysis, openOrApplyAiDetector, onAiDetectorModalClosed, applySavedAiAnalysis, persistAllergenConfiguration, getConfigurationTargetType, shouldOfferPropagation, refreshPendingPropagationSession, applyLoadedPropagationTargets, clearPendingPropagationSession, skipPropagationReview, openPropagationReviewModal, closePropagationReviewModal, onPropagationSelectAllChanged, updatePropagationTargetSelection, onPropagationTreeToggled, onPropagationTargetChangeToggled, applyPropagationChangesToTargets, isManualAllergenSelection, buildManualSelectedAllergen, buildAiSelectedAllergen, normalizeAllergenCode, normalizeAiAppliedSourceAllergenCode, isUserOverridePrediction, onTagAdded, cloneSelectedAllergen, buildPropagationTargetTree, buildPropagationTreeFromParents, createsPropagationParentCycle, buildPropagationTreeFromDepthHints, sortPropagationTree, flattenPropagationTargetTree, buildPropagationSelectionStateMap, addPropagationSelectionState, getPropagationParentId, getPropagationDepthHint, extractPropagationLineageIds, cloneSelectedAllergens, buildPropagationChanges, areSelectedAllergensEqual, normalizeAliasIds, loadPropagationTargetAllergens, ensurePropagationTargetAllergensLoaded, buildPropagationChangePreviewList, buildFallbackPropagationChangePreviewList, mergePropagationPreviewChanges, isPropagationRemovalChange, getPropagationChangeLabel, buildPropagationTargetChangeSets, mergePropagationChanges, onShowAllergenOnPrintoutChanged, debouncedFetchPreview, Toggle, AllergenPropagationTree, vModal, VAllergenMLModal, LabelLanguage }
}

})